/** @jsx jsx */

import { chain, pick, every, map, difference } from 'lodash';
import { Fragment, useEffect, useState } from 'react';

import { jsx } from '@emotion/core';
import { Margin } from '@zapier/design-system';

import HelpBox from 'app/developer-v3/components/HelpBox';
import KeyValueEditor, {
  KeyValue,
} from 'app/developer-v3/components/KeyValueEditor';
import ManagerLayout from 'app/developer-v3/layouts/ManagerLayout';
import SaveButtonGroup from 'app/developer-v3/components/SaveButtonGroup';
import { BASE_DOC_URL_VB } from 'app/developer-v3/constants';
import { useCurrentVersionReadOnlyReason } from 'app/developer-v3/hooks';

import type { EnvironmentVariableData } from 'app/entities/EnvironmentVariable/types';

// this is also ENVIRONMENT_VARIABLE_KEY_REGEX in developer_cli/constants.py
const validKeyRegex = new RegExp(/^[A-Z][A-Z0-9_]+$/i);
// this is also RESERVED_KEYS in developer_cli/constants.py
const reservedKeys = [
  '_HANDLER',
  '_X_AMZN_TRACE_ID',
  'AWS_DEFAULT_REGION',
  'AWS_REGION',
  'AWS_EXECUTION_ENV',
  'AWS_LAMBDA_FUNCTION_NAME',
  'AWS_LAMBDA_FUNCTION_MEMORY_SIZE',
  'AWS_LAMBDA_FUNCTION_VERSION',
  'AWS_LAMBDA_INITIALIZATION_TYPE',
  'AWS_LAMBDA_LOG_GROUP_NAME',
  'AWS_LAMBDA_LOG_STREAM_NAME',
  'AWS_ACCESS_KEY',
  'AWS_ACCESS_KEY_ID',
  'AWS_SECRET_ACCESS_KEY',
  'AWS_SESSION_TOKEN',
  'AWS_LAMBDA_RUNTIME_API',
  'LAMBDA_TASK_ROOT',
  'LAMBDA_RUNTIME_DIR',
];

const arrayToObj = (arr): Record<string, string> =>
  chain(arr)
    .map(v => [v.key, v.value])
    .fromPairs()
    .value();

type Props = {
  initialEnvVars: Array<EnvironmentVariableData>;
  saveEnv: (env: Record<string, string>) => void;
  isSaving: boolean;
};

const Environment = (props: Props) => {
  const { initialEnvVars, isSaving, saveEnv } = props;

  const [initEnvObj, setInitEnvObj] = useState<Record<string, string>>(
    arrayToObj(initialEnvVars)
  );
  const [envVars, setEnvVars] = useState<Array<KeyValue>>(
    initialEnvVars.map(v => pick(v, ['key', 'value']))
  );
  const [isFormValid, setIsFormValid] = useState<boolean>(true);

  const isReadOnly = !!useCurrentVersionReadOnlyReason();

  useEffect(() => {
    setEnvVars(initialEnvVars.map(v => pick(v, ['key', 'value'])));
    setInitEnvObj(arrayToObj(initialEnvVars));
  }, [initialEnvVars]);

  const onUpdate = (keyVars: Array<KeyValue>) => {
    const nextEnvVars = keyVars.map(kv => ({
      ...kv,
      isInvalidKey:
        !validKeyRegex.test(kv.key) || reservedKeys.includes(kv.key),
    }));

    setEnvVars(nextEnvVars);
    setIsFormValid(every(nextEnvVars, { isInvalidKey: false }));
  };

  const onSave = () => {
    const cleaned = {};

    envVars.forEach(({ key, value }) => {
      if (key && value && value !== initEnvObj[key]) {
        cleaned[key.trim()] = value.trim();
      }
    });

    const initKeys = Object.keys(initEnvObj);
    const keys = map(envVars, 'key');
    const keysDeleted = difference(initKeys, keys);
    keysDeleted.forEach(keyDeleted => (cleaned[keyDeleted] = ''));

    saveEnv(cleaned);
  };

  const items = envVars.map(({ key, value }) => ({
    key,
    value,
    isKeyReadOnly: Boolean(initEnvObj[key]),
  }));

  return (
    <ManagerLayout shouldLimitWidth={true} title="Environment Variables">
      <HelpBox
        description={
          <Fragment>
            Use environment variables to set secret values (such as API keys or
            client IDs and secrets), or to manage configurations and switch
            between staging and production environments. Environment variables
            are set per version.{' '}
            <a href={`${BASE_DOC_URL_VB}/build/env`} target="_blank">
              Learn more
            </a>
            .
          </Fragment>
        }
        heading="Set Variables for This Integration Version"
      />
      <Margin margin="20px 0px 0px 0px">
        <KeyValueEditor
          initialItems={items}
          isDisabled={isReadOnly}
          onUpdate={onUpdate}
          showHeaders={true}
          uppercaseKeys={true}
        />
        <SaveButtonGroup
          isDisabled={isReadOnly || !isFormValid}
          isSaving={isSaving}
          onSave={onSave}
        />
      </Margin>
    </ManagerLayout>
  );
};

Environment.defaultProps = {
  initialEnvVars: [],
};

export default Environment;
